import { useEffect, useMemo, useState } from 'react';
import {
  Notification,
  isMultiNotification,
  isSingleNotification,
  isBulkNotification,
} from 'src/types/Notifications';
import {
  isWatchedNotification,
  isFileOwnerNotification,
  isPropertyNotification,
} from 'src/utils/notificationFields';
import useLocalStorage from './useLocalStorage';

const AI_TYPE_GROUPS: Record<string, string[]> = {
  ai_all: [
    'event_notification',
    'renter_document_signed_thank_you',
    'automated_action_follow_up_required',
    'applicant_not_responding',
    'applicant_abandoned_certification',
  ],
  ai_event: ['event_notification', 'renter_document_signed_thank_you'],
  ai_action_required: [
    'automated_action_follow_up_required',
    'applicant_not_responding',
    'applicant_abandoned_certification',
  ],
};

interface FilterState {
  activeType: string;
  activeSource: string;
  activeReadStatus: string;
  activeNotificationType: string;
  filtersExpanded: boolean;
}

interface PersistedFilterState {
  activeType: string;
  activeSource: string;
  activeReadStatus: string;
  filtersExpanded: boolean;
}

interface FilterCounts {
  readCounts: { total: number; read: number; unread: number };
  typeReadCounts: Record<
    string,
    { total: number; read: number; unread: number }
  >;
  sourceReadCounts: Record<
    string,
    { total: number; read: number; unread: number }
  >;
  notificationTypeCounts: Record<
    string,
    { total: number; read: number; unread: number }
  >;
}

export const useNotificationFilters = (
  notifications: Notification[]
): {
  filterState: FilterState;
  filterCounts: FilterCounts;
  filteredNotifications: Notification[];
  handleReadStatusChange: (status: string) => void;
  handleSourceChange: (source: string) => void;
  handleTypeChange: (type: string) => void;
  handleNotificationTypeChange: (notificationType: string) => void;
  toggleFiltersExpanded: () => void;
} => {
  // Default filter state (persisted), stable for effect deps
  const defaultFilters = useMemo<PersistedFilterState>(
    () => ({
      activeType: 'all',
      activeSource: 'all',
      activeReadStatus: 'unread',
      filtersExpanded: false,
    }),
    []
  );

  // Migrate existing filters localStorage: drop activeNotificationType so we always load with "All Types"
  const migrateExistingFilters = (): PersistedFilterState => {
    const stored = localStorage.getItem('notificationFilters');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        const { activeNotificationType: _dropped, ...rest } = parsed;
        return { ...defaultFilters, ...rest } as PersistedFilterState;
      } catch {
        // ignore
      }
    }
    const existingExpanded = localStorage.getItem('filtersExpanded');
    if (existingExpanded !== null) {
      const migrated = {
        ...defaultFilters,
        filtersExpanded: existingExpanded === 'true',
      };
      localStorage.removeItem('filtersExpanded');
      return migrated;
    }
    return defaultFilters;
  };

  // Persistent filter state using useLocalStorage (no activeNotificationType)
  const [persistedFilters, setPersistedFilters] =
    useLocalStorage<PersistedFilterState>(
      'notificationFilters',
      migrateExistingFilters()
    );

  // One-time migration: if stored data still has activeNotificationType, strip it and write back
  useEffect(() => {
    const raw = persistedFilters as unknown as Record<string, unknown>;
    if (
      !('activeNotificationType' in raw) ||
      raw.activeNotificationType === undefined
    ) {
      return;
    }
    const { activeNotificationType: _dropped, ...rest } = raw;
    setPersistedFilters({ ...defaultFilters, ...rest } as PersistedFilterState);
  }, [defaultFilters, persistedFilters, setPersistedFilters]);

  // Types filter (Single/Bulk/Recertification) is not persisted so the page always loads with "All Types"
  const [activeNotificationType, setActiveNotificationType] =
    useState<string>('all');

  // Extract individual values from persisted state
  const { activeType, activeSource, activeReadStatus, filtersExpanded } =
    persistedFilters;

  // Reset to page 1 when filters change
  useEffect(() => {
    // This effect will be used by the parent component to reset pagination
  }, [activeType, activeSource, activeReadStatus, activeNotificationType]);

  // Group recert_tracking by property so UI shows one bulk row per property
  const displayNotifications = notifications;

  // Compute notification type counts with read/unread breakdown (single, bulk)
  const notificationTypeCounts = displayNotifications.reduce((acc, notif) => {
    let notificationType: string;
    const relatedType = notif.related_objects?.type;

    const isRecert =
      relatedType === 'recert_tracking' ||
      notif.notification_type === 'recert_notices_action_required';

    if (isSingleNotification(notif) && !isRecert) {
      notificationType = 'single_certification';
    } else if (
      isBulkNotification(notif) ||
      isMultiNotification(notif) ||
      isRecert
    ) {
      notificationType = 'bulk_certifications';
    } else {
      return acc;
    }

    if (!acc[notificationType]) {
      acc[notificationType] = { total: 0, read: 0, unread: 0 };
    }
    acc[notificationType].total += 1;
    if (notif.is_read) {
      acc[notificationType].read += 1;
    } else {
      acc[notificationType].unread += 1;
    }
    return acc;
  }, {} as Record<string, { total: number; read: number; unread: number }>);

  // Compute read/unread counts (from display list)
  const readCounts = displayNotifications.reduce(
    (acc, notif) => {
      acc.total += 1;
      if (notif.is_read) {
        acc.read += 1;
      } else {
        acc.unread += 1;
      }
      return acc;
    },
    { total: 0, read: 0, unread: 0 }
  );

  // Compute read/unread counts for each type
  const typeReadCounts = displayNotifications.reduce((acc, notif) => {
    const type = notif.notification_type || 'other';
    if (!acc[type]) {
      acc[type] = { total: 0, read: 0, unread: 0 };
    }
    acc[type].total += 1;
    if (notif.is_read) {
      acc[type].read += 1;
    } else {
      acc[type].unread += 1;
    }

    // Also count recert notifications under a virtual 'recert' type key
    const relatedType = notif.related_objects?.type;
    if (
      relatedType === 'recert_tracking' ||
      notif.notification_type === 'recert_notices_action_required'
    ) {
      if (!acc['recert']) {
        acc['recert'] = { total: 0, read: 0, unread: 0 };
      }
      acc['recert'].total += 1;
      if (notif.is_read) {
        acc['recert'].read += 1;
      } else {
        acc['recert'].unread += 1;
      }
    }

    return acc;
  }, {} as Record<string, { total: number; read: number; unread: number }>);

  // Add aggregated counts for AI type groups
  for (const [groupKey, memberTypes] of Object.entries(AI_TYPE_GROUPS)) {
    const grouped = { total: 0, read: 0, unread: 0 };
    for (const memberType of memberTypes) {
      const memberTypeCount = typeReadCounts[memberType];
      if (memberTypeCount) {
        grouped.total += memberTypeCount.total;
        grouped.read += memberTypeCount.read;
        grouped.unread += memberTypeCount.unread;
      }
    }
    typeReadCounts[groupKey] = grouped;
  }

  // Compute read/unread counts for each source
  const sourceReadCounts = displayNotifications.reduce((acc, notif) => {
    let source = 'file_owner'; // default
    if (isWatchedNotification(notif)) {
      source = 'watching';
    } else if (isPropertyNotification(notif)) {
      source = 'property';
    } else if (
      notif.is_system_notification ||
      notif.related_objects?.type === 'system'
    ) {
      source = 'system';
    }

    if (!acc[source]) {
      acc[source] = { total: 0, read: 0, unread: 0 };
    }
    acc[source].total += 1;
    if (notif.is_read) {
      acc[source].read += 1;
    } else {
      acc[source].unread += 1;
    }
    return acc;
  }, {} as Record<string, { total: number; read: number; unread: number }>);

  // Filter notifications by type, source, read status, and notification type
  const filteredNotifications = displayNotifications.filter((notification) => {
    const typeMatch =
      activeType === 'all' ||
      notification.notification_type === activeType ||
      (activeType === 'recert' &&
        (notification.related_objects?.type === 'recert_tracking' ||
          notification.notification_type ===
            'recert_notices_action_required')) ||
      (AI_TYPE_GROUPS[activeType] &&
        AI_TYPE_GROUPS[activeType].includes(notification.notification_type));
    const sourceMatch =
      activeSource === 'all' ||
      (activeSource === 'file_owner' &&
        isFileOwnerNotification(notification)) ||
      (activeSource === 'watching' && isWatchedNotification(notification)) ||
      (activeSource === 'property' && isPropertyNotification(notification)) ||
      (activeSource === 'system' && notification.is_system_notification);
    const readStatusMatch =
      activeReadStatus === 'all' ||
      (activeReadStatus === 'read' && notification.is_read) ||
      (activeReadStatus === 'unread' && !notification.is_read);
    const relatedType = notification.related_objects?.type;
    const isRecert =
      relatedType === 'recert_tracking' ||
      notification.notification_type === 'recert_notices_action_required';
    const isBulkDisplay =
      isBulkNotification(notification) ||
      isMultiNotification(notification) ||
      isRecert;
    const notificationTypeMatch =
      activeNotificationType === 'all' ||
      (activeNotificationType === 'single_certification' &&
        isSingleNotification(notification) &&
        !isRecert) ||
      (activeNotificationType === 'bulk_certifications' && isBulkDisplay);
    return typeMatch && sourceMatch && readStatusMatch && notificationTypeMatch;
  });

  // Filter handlers - update persistent state
  const handleReadStatusChange = (status: string): void => {
    setPersistedFilters((prev) => ({
      ...prev,
      activeReadStatus: status,
    }));
  };

  const handleSourceChange = (source: string): void => {
    setPersistedFilters((prev) => ({
      ...prev,
      activeSource: source,
    }));
  };

  const handleTypeChange = (type: string): void => {
    setPersistedFilters((prev) => ({
      ...prev,
      activeType: type,
    }));
  };

  const handleNotificationTypeChange = (notificationType: string): void => {
    setActiveNotificationType(notificationType);
  };

  const toggleFiltersExpanded = (): void => {
    setPersistedFilters((prev) => ({
      ...prev,
      filtersExpanded: !prev.filtersExpanded,
    }));
  };

  const filterState: FilterState = {
    activeType,
    activeSource,
    activeReadStatus,
    activeNotificationType,
    filtersExpanded,
  };

  const filterCounts: FilterCounts = {
    readCounts,
    typeReadCounts,
    sourceReadCounts,
    notificationTypeCounts,
  };

  return {
    // State
    filterState,
    filterCounts,
    filteredNotifications,

    // Handlers
    handleReadStatusChange,
    handleSourceChange,
    handleTypeChange,
    handleNotificationTypeChange,
    toggleFiltersExpanded,
  };
};
